import { HslAdjustmentFilter as PixiHslAdjustmentFilter } from 'pixi-filters';
import { assertNumberBetween } from '../../validators';
import { Config } from '../../video-data/config';
import type { BaseSettings } from '../TimeAwareBase';
import { Filter } from './Filter';
import { observable } from '../reactivity';

export class HslAdjustmentFilter extends Filter {
    static readonly NEUTRAL_HUE = 0;
    static readonly NEUTRAL_LIGHTNESS = 0;
    static readonly NEUTRAL_SATURATION = 0;

    override _classType = Config.HSL_ADJUSTMENT_FILTER;
    override pixiFilter: PixiHslAdjustmentFilter;
    @observable accessor #hue = HslAdjustmentFilter.NEUTRAL_HUE;
    @observable accessor #lightness = HslAdjustmentFilter.NEUTRAL_LIGHTNESS;
    @observable accessor #saturation = HslAdjustmentFilter.NEUTRAL_SATURATION;

    constructor(settings: BaseSettings) {
        super(settings);
        this.pixiFilter = new PixiHslAdjustmentFilter();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['hue', 'lightness', 'saturation']);
    }

    /**
     * Hue (-180 to 180)
     */
    get hue(): number {
        return this.#hue;
    }

    set hue(value: number) {
        if (value === this.#hue) {
            return;
        }
        assertNumberBetween(value, -180, 180);
        this.#hue = value;
        this.pixiFilter.hue = value;
        this.requiresUpdate = true;
    }

    /**
     * Lightness (-1 to 1)
     */
    get lightness(): number {
        return this.#lightness;
    }

    set lightness(value: number) {
        if (value === this.#lightness) {
            return;
        }
        assertNumberBetween(value, -1, 1);
        this.#lightness = value;
        this.pixiFilter.lightness = value;
        this.requiresUpdate = true;
    }

    /**
     * Saturation (-1 to 1)
     */
    get saturation(): number {
        return this.#saturation;
    }

    set saturation(value: number) {
        if (value === this.#saturation) {
            return;
        }
        assertNumberBetween(value, -1, 1);
        this.#saturation = value;
        this.pixiFilter.saturation = value;
        this.requiresUpdate = true;
    }

    override isNeutral(): boolean {
        return (
            this.hue === HslAdjustmentFilter.NEUTRAL_HUE &&
            this.lightness === HslAdjustmentFilter.NEUTRAL_LIGHTNESS &&
            this.saturation === HslAdjustmentFilter.NEUTRAL_SATURATION
        );
    }
}
